import { css } from "@emotion/react";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import { DateTime } from "@zapier/date-reporting";
import {
  AvatarWithTooltip,
  Badge,
  Checkbox,
  Colors,
  ServiceIcons,
  Spacer,
  Tag,
  Text,
} from "@zapier/design-system";
import { removeParamsFromUrl } from "@zapier/url-utils";

import ListItem from "@zapier/reporting/src/components/global/ListItem";
import { useFlags } from "@zapier/reporting/src/hooks";
import { ZapRun } from "@zapier/reporting/src/hooks/useRuns";
import {
  getResponsiveStyles,
  hiddenOnMobile,
  mediaQueries,
  visibleOnlyOnDesktop,
} from "@zapier/reporting/src/utils";
import Arrow from "./Arrow";
import PathIndicator from "./PathIndicator";
import BulkImportIndicator from "./BulkImportIndicator";
import ReplayIndicator from "./ReplayIndicator";
import RunStatus from "./RunStatus";
import TasksUsed from "./TasksUsed";

const ErrorLink = dynamic(() => import("./ErrorLink"), { ssr: false });

type Props = {
  isEmbed: boolean;
  isSelected: boolean;
  onSelect: (id: string, isSelected: boolean) => void;
  run: ZapRun;
  showOwner: boolean;
  showZapDetails: boolean;
};

function gridArea(area: string) {
  return css`
    grid-area: ${area};
  `;
}

export const embedMediaQueries = {
  mobile: `@media (min-width: 0)`,
  tablet: `@media (min-width: 660px)`,
  desktop: `@media (min-width: 800px)`,
};

const embedVisibleOnlyOnDesktop = getResponsiveStyles(
  {
    mobile: { display: "none", visibility: "hidden" },
    tablet: { display: "none", visibility: "hidden" },
    desktop: { display: "inherit", visibility: "visible" },
  },
  embedMediaQueries
);

const checkStyle = css`
  align-items: center;
  display: flex;
  grid-area: select;
  height: calc(100% + 30px);
  justify-content: center;
  margin-left: -19px;
  width: 51px;
`;

const infoStyle = css`
  display: grid;
  grid-area: info;
  grid-gap: 0px;
`;

const indicatorsStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
`;

const rootBaseStyle = css`
  align-items: center;
  color: ${Colors.GrayWarm10};
  display: grid;
  grid-gap: 15px;
  position: relative;
  padding: 4px;
`;

const titleStyle = getResponsiveStyles(
  {
    desktop: css`
      align-items: center;
      flex-direction: row;
    `,
    mobile: css`
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `,
  },
  mediaQueries
);

function rootStyle({ showOwner, showZapDetails, isEmbed }: Props) {
  const queries = isEmbed ? embedMediaQueries : mediaQueries;
  if (showOwner && showZapDetails) {
    // Showing owner, showing Zap details
    return getResponsiveStyles(
      {
        desktop: css`
          grid-template-areas: "select status apps triggerType info indicators tasks avatar arrow";
          grid-template-columns: 18px 160px 140px 45px 1fr auto 40px 40px 30px;
          grid-template-rows: 1fr;
        `,
        tablet: css`
          grid-template-areas: "select apps avatar" ". triggerType ." ". info indicators" ". status tasks";
          grid-template-columns: 18px 1fr auto;
          grid-template-rows: 40px 25px 1fr 40px;
        `,
        mobile: css`
          grid-template-areas: "select apps avatar" ". triggerType ." ". info info" ". status indicators";
          grid-template-columns: 18px 1fr auto;
          grid-template-rows: 40px 25px 1fr 40px;
        `,
      },
      queries
    );
  } else if (showOwner) {
    // Showing owner, hiding Zap details
    return getResponsiveStyles(
      {
        desktop: css`
          grid-template-areas: "select status triggerType info indicators tasks avatar arrow";
          grid-template-columns: 18px 160px 45px 1fr auto 40px 40px 30px;
          grid-template-rows: 1fr;
        `,
        tablet: css`
          grid-template-areas: "select triggerType avatar" ". info indicators" ". status tasks";
          grid-template-columns: 18px 1fr auto;
          grid-template-rows: 40px 1fr 40px;
        `,
        mobile: css`
          grid-template-areas: "select triggerType avatar" ". info info" ". status indicators";
          grid-template-columns: 18px 1fr auto;
          grid-template-rows: 40px 1fr 40px;
        `,
      },
      queries
    );
  } else if (showZapDetails) {
    // Hiding owner, showing Zap details
    return getResponsiveStyles(
      {
        desktop: css`
          grid-template-areas: "select status apps triggerType info indicators tasks arrow";
          grid-template-columns: 18px 160px 140px 45px 1fr auto 80px 30px;
          grid-template-rows: 1fr;
        `,
        tablet: css`
          grid-template-areas: "select apps apps" ". triggerType ." ". info indicators" ". status tasks";
          grid-template-columns: 18px 1fr auto;
          grid-template-rows: 40px 40px 1fr 40px;
        `,
        mobile: css`
          grid-template-areas: "select apps apps" ". triggerType ." ". info info" ". status indicators";
          grid-template-columns: 18px 1fr auto;
          grid-template-rows: 40px 40px 1fr 40px;
        `,
      },
      queries
    );
  }

  // Hiding owner, Hiding Zap details
  return getResponsiveStyles(
    {
      desktop: css`
        grid-template-areas: "select status triggerType info indicators tasks arrow";
        grid-template-columns: 18px 160px 45px 1fr auto 80px 40px;
        grid-template-rows: 1fr;
      `,
      tablet: css`
        grid-template-areas: "select info indicators" ". triggerType ." ". status tasks";
        grid-template-columns: 18px 1fr auto;
        grid-template-rows: 1fr 40px 40px;
      `,
      mobile: css`
        grid-template-areas: "select info info" ". triggerType ." ". status indicators";
        grid-template-columns: 18px 1fr auto;
        grid-template-rows: 1fr 40px 40px;
      `,
    },
    queries
  );
}

function RunRow(props: Props) {
  const t = useTranslations("run-log.run-row");
  const isDisabled = props.run.status === "delete";
  const { flags } = useFlags();
  const params = window.location.search;
  const replayCount = props.run.replayCount ?? 0;
  const showReplayIndicator =
    flags?.editor_run_replays_improvements && replayCount > 0;

  const services = useMemo(() => {
    return (props.run.zapApps ?? []).map((app) => ({
      name: app.name,
      src: app.images.url32X32,
    }));
  }, [props.run.zapApps]);

  const runId = props.run.id;
  const zapId = props.run.zap.id;
  const shouldRedirectToEditor = flags?.editor_redirect_to_zap_runs;

  const hrefToRunOrEditor = shouldRedirectToEditor
    ? `/editor/${zapId}/run/${runId}?sidebar=runs&filterRun=true`
    : `/app/history/${runId}${params}`;

  return (
    <ListItem
      href={removeParamsFromUrl(hrefToRunOrEditor, ["isBack"])}
      isReadOnly={isDisabled}
      isSelected={props.isSelected}
      onSelect={() => {
        props.onSelect(runId, !props.isSelected);
      }}
      target={
        shouldRedirectToEditor ? "_blank" : props.isEmbed ? "_top" : undefined
      }
    >
      <div css={[rootBaseStyle, rootStyle(props)]} data-testid="TaskRow">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <label
          css={checkStyle}
          htmlFor={runId + "select"}
          onClick={(e) => {
            // Since this is inside of a clickable element
            // we need to stop the checkbox click from propagating
            e.stopPropagation();
            return false;
          }}
        >
          <Checkbox
            aria-label={t("select-run-aria-label")}
            isSelected={props.isSelected}
            id={runId + "select"}
            onChange={(isSelected: boolean) => {
              props.onSelect(runId, isSelected);
            }}
            value={runId}
          />
        </label>
        <div css={gridArea("status")}>
          <RunStatus
            heldReason={props.run.heldReason}
            heldStaleAuth={props.run.heldStaleAuth}
            id={runId}
            status={props.run.status}
          />
        </div>
        <div css={infoStyle}>
          {props.showZapDetails && (
            <span css={titleStyle}>
              <Text color="GrayWarm10" type="Body4">
                {props.run.zap.title}
              </Text>
              {props.run.zap.kind === "transfer" && (
                <Tag variant="inverted" size="small">
                  {t("transfer")}
                </Tag>
              )}
            </span>
          )}
          <Text color="GrayWarm9" type="SmallPrint1">
            <DateTime
              date={props.run.startTime}
              format="MMM DD, YYYY hh:mm:ss a"
            />
          </Text>

          {props.run.status === "error" && (
            // div lets button keep its width
            <div>
              <Spacer height={8} />
              <ErrorLink
                runId={runId}
                zapId={zapId}
                shouldRedirectToEditor={shouldRedirectToEditor}
              />
            </div>
          )}
        </div>
        {props.showZapDetails && (
          <div css={gridArea("apps")}>
            <ServiceIcons maxServices={3} services={services} size="medium" />
          </div>
        )}
        {props.run.triggerType === "test" && (
          <div css={gridArea("triggerType")}>
            <Badge variant="primary">{t("test")}</Badge>
          </div>
        )}
        <div css={[gridArea("tasks"), hiddenOnMobile, { justifySelf: "end" }]}>
          <TasksUsed
            isError={
              props.run.billingAlignmentFailed === true &&
              props.run.triggerType !== "test"
            }
            used={
              props.run.billingAlignmentEnabled &&
              props.run.billingAlignedBillableCount === null &&
              props.run.triggerType !== "test"
                ? null
                : (props.run.billingAlignedBillableCount ??
                  props.run.billableCount ??
                  0)
            }
          />
        </div>
        <div css={[gridArea("indicators"), indicatorsStyle]}>
          {flags?.editor_bulk_zap_import &&
            props.run.triggerType === "bulk_import" && <BulkImportIndicator />}
          {showReplayIndicator && <ReplayIndicator replayCount={replayCount} />}
          {props.run.hasPaths && <PathIndicator />}
        </div>
        {props.showOwner && (
          <div css={[gridArea("avatar"), { justifySelf: "end" }]}>
            <AvatarWithTooltip
              name={props.run.zapOwner?.name || ""}
              size="medium"
              url={props.run.zapOwner?.imageUrl}
            />
          </div>
        )}
        <div
          css={[
            gridArea("arrow"),
            props.isEmbed ? embedVisibleOnlyOnDesktop : visibleOnlyOnDesktop,
          ]}
        >
          <Arrow />
        </div>
      </div>
    </ListItem>
  );
}

export default RunRow;
